import type { InvoiceTender, SettlementMethod } from "@/lib/api/types";

/**
 * A REFERENCE ON A CARD OR INSTAPAY TENDER (D163, F39 — founder 2026-10-15,
 * "(b) Optional ref (Rec.)").
 *
 * The terminal's approval code or the transfer's reference, typed on the
 * settle window when the cashier has one. Optional and NEVER required: an
 * empty field sends nothing, and Pay is never refused over it. The server
 * keeps it on that tender's own ledger row and refuses it on every other
 * method (`RESTAURANT_TENDER_REFERENCE_NOT_ALLOWED`), so the till only ever
 * sends it with CARD or INSTAPAY.
 */

/** The server's ceiling (`TENDER_REFERENCE_MAX_LENGTH`), after trimming. */
export const TENDER_REFERENCE_MAX = 32;

/** The two methods a reference may ride — the field shows for these only. */
export function takesTenderReference(method: SettlementMethod): boolean {
  return method === "CARD" || method === "INSTAPAY";
}

/**
 * What the server strips before anything else (`normalizeTenderReference`):
 * Unicode FORMAT characters (`\p{Cf}` — the U+200E/U+200F direction marks an
 * Arabic SMS or a banking app wraps around a pasted code, bidi embeddings and
 * overrides, ZWJ) and VARIATION SELECTORS. Invisible, so the cashier could
 * never see why Pay was refused — stripped here too, so the till never sends
 * one (6.5 gate R2).
 */
const INVISIBLE = /[\p{Cf}\p{Variation_Selector}]/gu;

/**
 * What the field drops AS TYPED (its `onChange`): a `|`, which the server
 * refuses because the CSV's `TenderRef` cell joins a bill's references on it
 * (6.5 gate R3), and control characters (a pasted tab), which it refuses as
 * not printable — so neither can ever turn into a refused Pay.
 */
const DROPPED_AS_TYPED = /[|\p{Cc}]/gu;

export function sanitizeTypedTenderReference(typed: string): string {
  return typed.replace(DROPPED_AS_TYPED, "");
}

/**
 * The payload field for one tender: `{ reference }` when the method takes one
 * and something was typed (invisible format characters stripped, trimmed),
 * else nothing at all — so a payload with no reference is byte-identical to
 * one from before D163, and a retry of the same tap fingerprints to the same
 * command id.
 */
export function tenderReferenceField(
  method: SettlementMethod,
  typed: string | undefined,
): { reference?: string } {
  if (!takesTenderReference(method)) return {};
  const reference = (typed ?? "").replace(INVISIBLE, "").trim();
  return reference === "" ? {} : { reference };
}

/**
 * Whether the settle window holds a reference that WOULD be sent — the
 * discard guard's question. A code typed on Card and left behind after the
 * switch to Cash is not sent, so it is not something a swipe would lose
 * (6.5 gate R5).
 */
export function hasPendingTenderReference(
  method: SettlementMethod,
  typed: string | undefined,
): boolean {
  return tenderReferenceField(method, typed).reference !== undefined;
}

/** Every reference on a bill, with its method, in the order the money arrived. */
export function tenderReferencesOf(
  tenders: readonly InvoiceTender[],
): { method: SettlementMethod; reference: string }[] {
  return tenders.flatMap((tender) =>
    (tender.references ?? []).map((reference) => ({
      method: tender.method,
      reference,
    })),
  );
}
